import { addDays } from "./time";
import type { EpochSeconds, LocalDate } from "./time";

/** The server's threshold for an open working day (F-13, ADR-0128). */
export interface OpenDayThreshold {
  /** Date of the working day, the day its shift began (ADR-0007). */
  readonly date: LocalDate;
  /**
   * Instant from which the day counts as incomplete on the server (`OpenWorkDayPolicy`): the start
   * of the next day, but not before the open shift can no longer realistically be running.
   */
  readonly incompleteFrom: EpochSeconds;
}

/** Input of {@link openDaysToCorrect}. */
export interface OpenDaysToCorrectInput {
  /** Open days reported by the server. */
  readonly openDays: readonly LocalDate[];
  /** The server's thresholds of the open days; empty from older servers or stored snapshots. */
  readonly thresholds: readonly OpenDayThreshold[];
  /** Date of the current working day, if any. */
  readonly currentDay: LocalDate | null;
  /** Local date of today. */
  readonly today: LocalDate;
  /** Current instant. */
  readonly now: EpochSeconds;
}

/**
 * Returns the open days the person is asked to correct. An open day with a threshold from the
 * server counts from that instant on (F-13), the same instant from which the server reports the day
 * as incomplete and sends the reminder "Arbeitstag nicht abgeschlossen": a day shift whose "Gehen"
 * was forgotten from the next day on, a night shift only once it can no longer be running. Without
 * a threshold (a snapshot of an earlier app version, an older server) the previous rule applies:
 * every open day except the current working day, and the current one as well once it began before
 * yesterday (F-11, ADR-0126). A working day is the day its shift began (ADR-0007). The clock screen
 * lists these days and the icon badge counts them (F-14).
 *
 * @param input - Open days, thresholds, current working day, today and now.
 * @returns The dates to correct.
 */
export function openDaysToCorrect(input: OpenDaysToCorrectInput): LocalDate[] {
  const yesterday = addDays(input.today, -1);
  const thresholds = new Map(
    input.thresholds.map((threshold) => [threshold.date, threshold.incompleteFrom]),
  );
  const dates = input.openDays.filter((date) => {
    const incompleteFrom = thresholds.get(date);
    const toCorrect =
      incompleteFrom === undefined
        ? date !== input.currentDay || date < yesterday
        : input.now >= incompleteFrom;

    return toCorrect;
  });

  return dates;
}
